import React, { useEffect, useState } from "react";
import { api, errText } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wand2, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const Stat = ({ label, value }) => (
  <div className="border border-black/10 bg-white p-6">
    <div className="font-mono-eyebrow text-black/50">{label}</div>
    <div className="font-serif-display text-5xl mt-1" data-testid={`digest-${label.toLowerCase().replace(/\s/g,"-")}`}>{value}</div>
  </div>
);

const Digest = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/digest").then(r => setD(r.data)).catch(e => setErr(errText(e)));
  }, []);

  if (err) return <div className="p-20 text-center font-serif-display text-3xl">{err}</div>;
  if (!d) return <div className="p-20 text-center font-mono-eyebrow">Compiling your week…</div>;

  const monday = new Date(d.week_start);
  const now = new Date(d.generated_at);
  const range = `${monday.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${now.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  const empty = d.outfits.length === 0 && d.tryons_count === 0;

  return (
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
      <div className="border-b border-black/10 pb-8 mb-10 grid grid-cols-12 gap-6 items-end">
        <div className="col-span-12 md:col-span-8">
          <div className="font-mono-eyebrow">This week · {range}</div>
          <h1 className="font-serif-display text-6xl md:text-8xl leading-none mt-3">The Digest</h1>
          <p className="mt-4 text-black/60 max-w-md">A quiet summary of the looks you built, the tries you took, and one Muse-styled idea for the days ahead.</p>
        </div>
        <div className="col-span-12 md:col-span-4 text-black/40 font-mono-eyebrow md:text-right">
          Issue N° {Math.ceil((now - new Date(now.getFullYear(),0,1)) / (7*24*3600*1000))}
        </div>
      </div>

      {empty ? (
        <div className="py-20 text-center">
          <div className="font-serif-display text-4xl">Nothing yet — a fresh page.</div>
          <div className="text-black/60 mt-3">Try on something new in the Studio and it'll turn up here next Sunday.</div>
          <Link to="/studio" className="btn-ink mt-8 inline-block">Enter the studio</Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
            <Stat label="Tryons" value={d.tryons_count}/>
            <Stat label="Outfits saved" value={d.outfits.length}/>
            <Stat label="Remixes received" value={d.remixes_received}/>
            <Stat label="Wardrobe pieces" value={d.wardrobe_count}/>
          </div>

          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-7">
              {d.hero_tryon ? (
                <div className="aspect-[4/5] overflow-hidden bg-white polaroid">
                  <img src={d.hero_tryon.result_data_url} alt="hero" className="w-full h-full object-cover"/>
                </div>
              ) : d.hero_items.length ? (
                <div className="grid grid-cols-3 gap-2">
                  {d.hero_items.slice(0, 9).map((it, i) => (
                    <motion.div key={it.id}
                      initial={{opacity:0, y:12}} animate={{opacity:1, y:0}}
                      transition={{delay: i*0.04}}
                      className="aspect-[3/4] overflow-hidden bg-white border border-black/5">
                      <img src={it.image_url} alt="" className="w-full h-full object-cover"/>
                    </motion.div>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="col-span-12 md:col-span-5">
              <div className="font-mono-eyebrow mb-4 text-[color:var(--accent)]">Looks you saved</div>
              <h2 className="font-serif-display text-3xl mb-6">Your week in silhouettes</h2>
              <ul className="space-y-3">
                {d.outfits.map(o => (
                  <li key={o.id}>
                    <Link to={`/outfits/${o.share_slug}`} className="flex items-center justify-between py-3 border-b border-black/10 group">
                      <div className="min-w-0">
                        <div className="font-serif-display text-lg leading-tight truncate">{o.name}</div>
                        <div className="font-mono-eyebrow text-black/40 text-[10px]">{new Date(o.created_at).toLocaleDateString(undefined,{weekday:"long", day:"2-digit", month:"short"})}</div>
                      </div>
                      <span className="editorial-link font-mono-eyebrow">Open →</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-black/10 text-sm text-black/60">
                Lifetime archive: <span className="font-mono-eyebrow">{d.total_outfits} outfits</span>
              </div>
            </div>
          </div>

          {/* Muse suggestion */}
          <div className="relative border border-black/10 bg-gradient-to-br from-white to-[#F1EFE7] p-8 md:p-12 grain overflow-hidden">
            <div className="relative z-10 grid grid-cols-12 gap-6 items-center">
              <div className="col-span-12 md:col-span-8">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-black text-[#F6F5F1] flex items-center justify-center"><Wand2 className="w-4 h-4"/></div>
                  <div className="font-mono-eyebrow">Muse · A look for next week</div>
                </div>
                <h3 className="font-serif-display text-4xl md:text-5xl leading-tight mt-3">"{d.muse_suggestion.occasion}"</h3>
                <p className="text-black/60 mt-3 max-w-md">Muse can compose an outfit for this from your archive in one tap. It will pick the pieces, the pose, and the scene — you review the fit.</p>
              </div>
              <div className="col-span-12 md:col-span-4 md:text-right">
                <button
                  data-testid="digest-muse-cta"
                  disabled={!d.muse_suggestion.can_run}
                  onClick={()=>nav(`/studio?occasion=${encodeURIComponent(d.muse_suggestion.occasion)}`)}
                  className="btn-ink inline-flex items-center gap-2 disabled:opacity-50">
                  <Sparkles className="w-3.5 h-3.5"/> Style with Muse
                </button>
                {!d.muse_suggestion.can_run && (
                  <div className="text-xs text-black/50 mt-2">Add a few pieces to your wardrobe first</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Digest;
